/**
 * Usage-alerts cross-service contracts.
 *
 * Frozen in phase 0 of `plans/usage-alerts/`. Consumed by:
 *   - `apps/webhooks` evaluator cron (phase 2) — emits events keyed
 *     on these axes / thresholds.
 *   - `apps/consumer` email channel adapter (phase 3) — renders
 *     templates off `UsageThresholdEventData`.
 *   - `apps/api` management routes (phase 4) — exposes axis config
 *     in the Notifications UI.
 *   - `packages/emails` — template prop types derive from
 *     `UsageThresholdEventData`.
 *
 * The schemas here are additive to `webhook-endpoints.ts` — the
 * envelope shape is unchanged; only the `data` field's contents are
 * new.
 */
import { z } from 'zod';

// ─── Axes ──────────────────────────────────────────────────────────────

/**
 * Axes we alert on. Frozen list — adding an axis is a phase 0-level
 * change (new plan-limit mapping + new event-type string + migration
 * check-constraint widen). Not something to do inline.
 *
 * `extra_seats` deliberately excluded: it bills on overage, not a cap
 * — there is no threshold to warn about. See README guardrail 4.
 */
export const USAGE_ALERT_AXES = [
  'on_demand_audits',
  'daily_sites_scheduled',
  'hourly_sites_scheduled',
  'spending_budget',
] as const;
export type UsageAlertAxis = (typeof USAGE_ALERT_AXES)[number];

/**
 * Thresholds the legacy plan-limit axes fire at. 100 % is the hard cap
 * enforced at the API gate (`402 QUOTA_EXCEEDED`), so no third
 * threshold — crossing 100 % is an error, not a warning.
 *
 * The `spending_budget` axis ignores this list: its thresholds are
 * per-org (`credit_balances.budget_alert_thresholds`, any 1–100
 * percentages, at most `BUDGET_ALERT_MAX_THRESHOLDS`).
 */
export const USAGE_ALERT_THRESHOLDS = [80, 90] as const;
export type UsageAlertThreshold = (typeof USAGE_ALERT_THRESHOLDS)[number];

/** Upper bound on `budget_alert_thresholds` entries (shared validation). */
export const BUDGET_ALERT_MAX_THRESHOLDS = 5;

/**
 * Period-kind semantics for the `billing_period` key on
 * `usage_alert_state`:
 *
 * - `mtd` — `YYYY-MM` (UTC). Rows become inert on month rollover; a
 *   fresh period fires fresh events.
 * - `point` — literal string `'point'`. Rows are cleared via
 *   `cleared_at` when usage drops below the threshold, allowing a
 *   subsequent climb to re-fire. See phase 0 §3.2.
 * - `cycle` — the wallet's billing cycle: `cycle_expires_at` ISO
 *   string (falls back to `YYYY-MM` when unset). A cycle grant changes
 *   the key, so a fresh period fires fresh events — mirroring the
 *   `credits_spent_this_period` reset.
 */
export type UsageAlertPeriodKind = 'mtd' | 'point' | 'cycle';

/**
 * Map from axis → (plan-limits key, counter source, period-kind).
 *
 * Adding a new axis is a single entry here plus a new event-type
 * string in `WEBHOOK_EVENT_TYPES` plus widening the check constraint
 * on `usage_alert_state.axis`. The evaluator (phase 2) iterates this
 * map — it does not hardcode axis names.
 */
export const USAGE_ALERT_AXIS_CONFIG = {
  on_demand_audits: {
    /** Human-readable label used in the email subject / Slack header. */
    label: 'on-demand audits',
    /** Plan-limits key that holds the included allowance. */
    plan_limit_key: 'max_on_demand_per_month',
    period_kind: 'mtd',
  },
  daily_sites_scheduled: {
    label: 'daily scheduled-site slots',
    plan_limit_key: 'included_daily_sites_scheduled',
    period_kind: 'point',
  },
  hourly_sites_scheduled: {
    label: 'hourly scheduled-site slots',
    plan_limit_key: 'included_hourly_sites_scheduled',
    period_kind: 'point',
  },
  spending_budget: {
    label: 'spending budget',
    // Allowance comes from `credit_balances.budget_credits`, not plan
    // limits — the generic plan-limit evaluator loop skips null keys.
    plan_limit_key: null,
    period_kind: 'cycle',
  },
} as const satisfies Record<
  UsageAlertAxis,
  {
    label: string;
    plan_limit_key:
      | 'max_on_demand_per_month'
      | 'included_daily_sites_scheduled'
      | 'included_hourly_sites_scheduled'
      | null;
    period_kind: UsageAlertPeriodKind;
  }
>;

/** Event type emitted when usage crosses the 80 % threshold. */
export const USAGE_THRESHOLD_WARNING_EVENT = 'usage.threshold.warning' as const;
/** Event type emitted when usage crosses the 90 % threshold. */
export const USAGE_THRESHOLD_CRITICAL_EVENT = 'usage.threshold.critical' as const;

/**
 * Map threshold → event-type literal. Evaluator uses this to stamp
 * the correct `event` on the envelope without branching on the
 * number.
 */
export const USAGE_THRESHOLD_EVENT_BY_THRESHOLD = {
  80: USAGE_THRESHOLD_WARNING_EVENT,
  90: USAGE_THRESHOLD_CRITICAL_EVENT,
} as const satisfies Record<
  UsageAlertThreshold,
  typeof USAGE_THRESHOLD_WARNING_EVENT | typeof USAGE_THRESHOLD_CRITICAL_EVENT
>;

// ─── Payload schema ────────────────────────────────────────────────────

/**
 * `data` field of a `usage.threshold.warning` / `usage.threshold.critical`
 * webhook envelope. The outer envelope
 * (`WebhookEventEnvelopeSchema`) is unchanged.
 */
export const UsageThresholdEventDataSchema = z.object({
  axis: z.enum(USAGE_ALERT_AXES),
  /**
   * Percentage crossed. Legacy axes fire at the fixed 80/90; the
   * `spending_budget` axis fires at per-org custom thresholds, so any
   * 1–100 integer is valid on the wire.
   */
  threshold: z.number().int().min(1).max(100),
  /** Absolute count at fire time (e.g. 81 on-demand audits MTD). */
  usage: z.number().int().nonnegative(),
  /** Included allowance at fire time (e.g. 100). */
  included: z.number().int().nonnegative(),
  /** Rounded to 1 dp. e.g. 81.5 — not 81.523913. */
  usage_percent: z.number(),
  /** `YYYY-MM` (UTC) for mtd axes, `'point'` for point-in-time axes. */
  billing_period: z.string(),
  /** `'indie'`, `'startup'`, etc. Nullable for orgs on a legacy/unknown plan. */
  plan: z.string().nullable(),
  /**
   * Dashboard deep-link used in the email CTA. Billing page for the
   * plan-limit axes; the Budgets settings page on `spending_budget`
   * events (reviewing the budget is the mitigation, not upgrading).
   */
  upgrade_url: z.string().url(),
  /**
   * Dashboard site-settings deep-link. Populated by the evaluator only
   * for scheduled-site axes (`daily_sites_scheduled`,
   * `hourly_sites_scheduled`); on `on_demand_audits` events it's
   * omitted because the user can't mitigate the cap by
   * pausing/unscheduling sites.
   *
   * Optional — old subscribers replaying old envelopes must not 400.
   * Templates default to `${dashboardOrigin}/app/sites` if absent on
   * a scheduled-site-axis event (defensive — older webhook replays).
   */
  manage_audit_config_url: z.string().url().optional(),
  /** `max(0, included - usage)`. Pre-computed so templates don't re-math. */
  remaining: z.number().int().nonnegative(),
  /**
   * Present only on `spending_budget` events: 'soft' (alert only) or
   * 'hard' (further paid usage is blocked once the budget is spent) —
   * lets templates say what happens at 100 %.
   */
  budget_mode: z.enum(['soft', 'hard']).optional(),
});
export type UsageThresholdEventData = z.infer<typeof UsageThresholdEventDataSchema>;
